import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { prepareCodesForSubmit, RangeTooLargeError } from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import RuleHistory from "pages/PayerRules/components/RuleHistory";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetModifierAdjustmentByIdQuery,
  useUpdateModifierAdjustmentMutation,
} from "store/queries/modifierAdjustments";

import {
  IProfessionalModifierFormData,
  INITIAL_PROFESSIONAL_MODIFIER_FORM_DATA,
  ProfessionalModifierForm,
} from "./components/ProfessionalModifierForm";

export function EditProfessionalModifierPage() {
  useDocumentTitle("Edit Professional Modifier Rule");
  const navigate = useNavigate();
  const { id } = useParams();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data, isLoading: isFetching } = useGetModifierAdjustmentByIdQuery(
    id ?? ""
  );
  const [updateMutation, { isLoading: isUpdating }] =
    useUpdateModifierAdjustmentMutation();
  const [formData, setFormData] = useState<IProfessionalModifierFormData>(
    INITIAL_PROFESSIONAL_MODIFIER_FORM_DATA
  );

  useEffect(() => {
    const ruleset = data?.data;
    if (!ruleset) return;
    setFormData({
      modifier: ruleset.modifier,
      multiplier: Number(ruleset.multiplier),
      providerId: ruleset.providerId,
      payerId: ruleset.payerId,
      cptCodes: ruleset.cptCodes ?? [],
      groupName: ruleset.groupName ?? "",
      isDefault: ruleset.isDefault,
    });
  }, [data]);

  const handleSubmit = async () => {
    if (!id) return;
    if (!formData.modifier.trim()) return;
    if (!formData.isDefault && !formData.providerId) return;
    let cptCodes: string[];
    try {
      cptCodes = prepareCodesForSubmit(formData.cptCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Range too large",
        detail:
          err instanceof RangeTooLargeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      await updateMutation({
        id,
        modifier: formData.modifier.trim().toUpperCase(),
        multiplier: formData.multiplier,
        cptCodes,
        groupName: formData.groupName.trim() || null,
        isDefault: formData.isDefault,
        providerId: formData.isDefault ? null : formData.providerId,
        payerId: formData.isDefault ? null : formData.payerId,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Updated!",
        detail: "Professional modifier rule updated successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message || "Failed to update professional modifier rule",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingSpinner />;

  return (
    <>
      <ProfessionalModifierForm
        title="Edit Professional Modifier Rule"
        submitLabel="Save Changes"
        formData={formData}
        onChange={setFormData}
        onSubmit={handleSubmit}
        isSubmitting={isUpdating}
        scopeLocked
      />
      <RuleHistory histories={data?.data.history} />
    </>
  );
}
